import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";
import {
  Shield, Plus, Folder, FolderPlus, FileText, Trash2, LogOut,
  ChevronRight, Save, BookmarkPlus, MoreVertical, Star, Loader2, Pencil,
  Share2, Copy as CopyIcon, ExternalLink,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import SheetCanvas from "@/components/sheet/SheetCanvas";
import { defaultBoxes, defaultSilhouette, newCustomBox } from "@/lib/defaultTemplate";

export default function AppShell() {
  const { user, logout } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();

  const [folders, setFolders] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null); // null = all
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareListOpen, setShareListOpen] = useState(false);
  const [shareList, setShareList] = useState([]);
  const [shareListLoading, setShareListLoading] = useState(false);

  const debounceRef = useRef(null);

  const loadLists = useCallback(async () => {
    const [f, ch, tp] = await Promise.all([
      api.get("/folders"),
      api.get("/characters", { params: { kind: "character" } }),
      api.get("/characters", { params: { kind: "template" } }),
    ]);
    setFolders(f.data);
    setCharacters(ch.data);
    setTemplates(tp.data);
  }, []);

  const loadSheet = useCallback(async (sid) => {
    if (!sid) { setSheet(null); return; }
    const { data } = await api.get(`/characters/${sid}`);
    setSheet(data);
  }, []);

  // Initial load
  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadLists();
      if (id) {
        await loadSheet(id);
      } else {
        const { data } = await api.get("/me/initial-sheet");
        if (data.sheet_id) nav(`/app/sheet/${data.sheet_id}`, { replace: true });
        else {
          // No characters yet — create a starter
          const { data: created } = await api.post("/characters", {
            name: "Untitled Sheet", kind: "character",
            boxes: defaultBoxes(), silhouette: defaultSilhouette(),
          });
          await loadLists();
          nav(`/app/sheet/${created.id}`, { replace: true });
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Autosave
  const saveSheet = useCallback(async (next, opts = {}) => {
    setSaving(true);
    try {
      await api.put(`/characters/${next.id}`, {
        name: next.name, boxes: next.boxes, silhouette: next.silhouette, canvas: next.canvas, folder_id: next.folder_id,
      });
      if (!opts.silent) toast.success("Saved");
      await loadLists();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }, [loadLists]);

  const onSheetChange = (next) => {
    setSheet(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveSheet(next, { silent: true }), 700);
  };

  const filteredCharacters = useMemo(() => {
    if (activeFolder === null) return characters;
    return characters.filter((c) => (c.folder_id || null) === activeFolder);
  }, [characters, activeFolder]);

  const MAX = 5;
  const charsFull = characters.length >= MAX;
  const tplsFull = templates.length >= MAX;

  const handleLimitError = (e, fallback) => {
    const detail = e?.response?.data?.detail;
    toast.error(typeof detail === "string" ? detail : (fallback || "Something went wrong"));
  };

  // Actions
  const createCharacter = async (fromTemplate = null) => {
    if (charsFull) {
      toast.error(`You've reached the limit of ${MAX} sheets. Delete one to make room.`);
      return;
    }
    const base = fromTemplate || { boxes: defaultBoxes(), silhouette: defaultSilhouette() };
    try {
      const { data } = await api.post("/characters", {
        name: "Untitled Sheet", kind: "character", folder_id: activeFolder,
        boxes: base.boxes, silhouette: base.silhouette,
      });
      await loadLists();
      nav(`/app/sheet/${data.id}`);
    } catch (e) {
      handleLimitError(e, "Could not create sheet");
    }
  };

  const saveAsTemplate = async () => {
    if (!sheet) return;
    if (tplsFull) {
      toast.error(`You've reached the limit of ${MAX} templates. Delete one to make room.`);
      return;
    }
    const name = window.prompt("Template name", sheet.name + " template");
    if (!name) return;
    try {
      await api.post("/characters", {
        name, kind: "template",
        boxes: sheet.boxes, silhouette: sheet.silhouette, canvas: sheet.canvas,
      });
      toast.success("Saved as template");
      await loadLists();
    } catch (e) {
      handleLimitError(e, "Could not save template");
    }
  };

  const deleteSheet = async (sid) => {
    if (!window.confirm("Delete this sheet permanently?")) return;
    await api.delete(`/characters/${sid}`);
    await loadLists();
    if (sid === sheet?.id) {
      const { data } = await api.get("/me/initial-sheet");
      if (data.sheet_id) nav(`/app/sheet/${data.sheet_id}`);
      else nav(`/app`);
    }
  };

  const cloneSheet = async (sid) => {
    if (charsFull) {
      toast.error(`You've reached the limit of ${MAX} sheets. Delete one to make room.`);
      return;
    }
    try {
      const { data } = await api.post(`/characters/${sid}/clone`);
      await loadLists();
      nav(`/app/sheet/${data.id}`);
    } catch (e) {
      handleLimitError(e, "Could not clone sheet");
    }
  };

  const newFolder = async () => {
    const name = window.prompt("Folder name");
    if (!name) return;
    await api.post("/folders", { name: name.trim() });
    await loadLists();
  };

  const renameFolder = async (fid, currentName) => {
    const name = window.prompt("Rename folder", currentName);
    if (!name || !name.trim() || name.trim() === currentName) return;
    await api.put(`/folders/${fid}`, { name: name.trim() });
    await loadLists();
  };

  const deleteFolder = async (fid) => {
    if (!window.confirm("Delete folder? Characters inside will be unfiled.")) return;
    await api.delete(`/folders/${fid}`);
    if (activeFolder === fid) setActiveFolder(null);
    await loadLists();
  };

  const renameSheet = async (val) => {
    if (!sheet || !val.trim()) return setRenaming(false);
    const next = { ...sheet, name: val.trim() };
    setSheet(next);
    setRenaming(false);
    await saveSheet(next);
  };

  const moveToFolder = async (fid) => {
    if (!sheet) return;
    const next = { ...sheet, folder_id: fid };
    setSheet(next);
    await saveSheet(next);
  };

  const setAsStart = async () => {
    if (!sheet) return;
    await api.post("/me/start-sheet", { sheet_id: sheet.id });
    toast.success("Set as your starting sheet");
  };

  const shareSheet = async () => {
    if (!sheet) return;
    // Flush any pending autosave so the snapshot is current
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    await saveSheet(sheet, { silent: true });
    try {
      const { data } = await api.post(`/characters/${sheet.id}/share`);
      const url = `${window.location.origin}/shared/${data.token}`;
      setShareUrl(url);
      // Best-effort clipboard copy
      try { await navigator.clipboard.writeText(url); } catch { /* dialog still shows link */ }
    } catch (e) {
      toast.error("Could not create share link");
    }
  };

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — select the link and copy manually");
    }
  };

  const openShareList = async () => {
    setShareListOpen(true);
    setShareListLoading(true);
    try {
      const { data } = await api.get("/shares");
      setShareList(data);
    } catch {
      toast.error("Could not load share links");
    } finally {
      setShareListLoading(false);
    }
  };

  const revokeShare = async (token) => {
    if (!window.confirm("Revoke this link? Anyone using it will see an 'expired link' message.")) return;
    try {
      await api.delete(`/shares/${token}`);
      setShareList((prev) => prev.filter((s) => s.token !== token));
      toast.success("Share link revoked");
    } catch {
      toast.error("Could not revoke link");
    }
  };

  const copyToClipboard = async (text, label = "Link") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const addBox = () => {
    if (!sheet) return;
    const next = { ...sheet, boxes: [...sheet.boxes, newCustomBox(40, 40)] };
    setSheet(next);
    saveSheet(next, { silent: true });
  };

  return (
    <div className="flex min-h-screen bg-[#070A11] text-white">
      <Toaster theme="dark" position="bottom-right" />
      <Dialog open={!!shareUrl} onOpenChange={(o) => { if (!o) setShareUrl(""); }}>
        <DialogContent className="border-[#212A3E] bg-[#0d1320] text-white sm:max-w-lg" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Share this sheet</DialogTitle>
            <DialogDescription className="text-[#A0ABC0]">
              Anyone with this link can view a read-only snapshot of your sheet — exactly as saved right now. Paste it into a message, email, or AI chat.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3">
            <div className="flex gap-2">
              <Input
                value={shareUrl}
                readOnly
                onFocus={(e) => e.target.select()}
                className="border-[#212A3E] bg-[#070A11] text-white"
                data-testid="share-link-input"
              />
              <Button
                onClick={copyShareUrl}
                className="bg-[#D4AF37] text-[#0b0f18] hover:bg-[#e7c75a]"
                data-testid="share-copy-btn"
              >
                <CopyIcon className="mr-1 h-4 w-4" /> Copy
              </Button>
            </div>
            <p className="mt-3 text-xs text-[#A0ABC0]">
              Tip: this is a snapshot — future edits won't change what people see at this link. Share again for an updated version.
            </p>
          </div>
          <DialogFooter className="mt-2">
            <Button
              variant="outline"
              onClick={() => window.open(shareUrl, "_blank", "noopener")}
              className="border-[#212A3E] bg-transparent text-white hover:bg-white/5"
              data-testid="share-open-btn"
            >
              <ExternalLink className="mr-1 h-4 w-4" /> Open preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shareListOpen} onOpenChange={setShareListOpen}>
        <DialogContent className="border-[#212A3E] bg-[#0d1320] text-white sm:max-w-2xl" data-testid="share-list-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Your share links</DialogTitle>
            <DialogDescription className="text-[#A0ABC0]">
              Every link auto-expires one year after it was created. Revoke any link to immediately invalidate it.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 max-h-[60vh] overflow-y-auto">
            {shareListLoading ? (
              <div className="flex items-center gap-2 px-2 py-6 text-sm text-[#A0ABC0]">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : shareList.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-[#A0ABC0]">No active share links yet.</p>
            ) : (
              <ul className="divide-y divide-[#212A3E]">
                {shareList.map((s) => {
                  const url = `${window.location.origin}/shared/${s.token}`;
                  const created = s.created_at ? new Date(s.created_at) : null;
                  const expires = s.expires_at ? new Date(s.expires_at) : null;
                  const daysLeft = expires ? Math.max(0, Math.ceil((expires - Date.now()) / 86400000)) : null;
                  return (
                    <li key={s.token} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-4" data-testid={`share-item-${s.token}`}>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-serif-display text-base text-white">{s.name || "Untitled"}</div>
                        <div className="font-mono-tight text-[10px] uppercase tracking-[0.18em] text-[#A0ABC0]">
                          {created ? `Created ${created.toLocaleDateString()}` : ""}
                          {daysLeft != null && (
                            <>{" · "}<span className={daysLeft <= 30 ? "text-[#D4AF37]" : ""}>Expires in {daysLeft} day{daysLeft === 1 ? "" : "s"}</span></>
                          )}
                        </div>
                        <div className="mt-1 truncate text-xs text-[#A0ABC0]/80">{url}</div>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(url, "Link")}
                          className="text-white hover:text-[#D4AF37]"
                          data-testid={`share-item-${s.token}-copy`}
                        >
                          <CopyIcon className="mr-1 h-3 w-3" /> Copy
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(url, "_blank", "noopener")}
                          className="text-white hover:text-[#D4AF37]"
                        >
                          <ExternalLink className="mr-1 h-3 w-3" /> Open
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => revokeShare(s.token)}
                          className="text-[#A0ABC0] hover:text-red-400"
                          data-testid={`share-item-${s.token}-revoke`}
                        >
                          <Trash2 className="mr-1 h-3 w-3" /> Revoke
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Sidebar */}
      <aside className="hidden w-72 shrink-0 border-r border-[#212A3E] bg-[#0a0f1c] md:flex md:flex-col" data-testid="sidebar">
        <div className="flex items-center gap-2 border-b border-[#212A3E] px-5 py-5">
          <Shield className="h-5 w-5 text-[#D4AF37]" />
          <span className="font-serif-display text-xl text-white">RPG<span className="text-[#D4AF37]">me</span></span>
        </div>

        <div className="px-4 py-3">
          <Button
            onClick={() => createCharacter()}
            disabled={charsFull}
            className="w-full bg-[#D4AF37] text-[#0b0f18] hover:bg-[#e7c75a] disabled:bg-[#D4AF37]/30 disabled:cursor-not-allowed"
            data-testid="new-character-btn"
            title={charsFull ? `Sheet limit reached (${characters.length}/${MAX})` : "Create a new sheet"}
          >
            <Plus className="mr-2 h-4 w-4" /> New sheet
          </Button>
          {charsFull && (
            <p className="mt-2 text-center text-[10px] uppercase tracking-[0.18em] text-[#A0ABC0]" data-testid="sheets-limit-hint">
              {characters.length}/{MAX} sheets used
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-6">
          <SectionHeader label="Folders" action={<button onClick={newFolder} className="text-[#A0ABC0] hover:text-[#D4AF37]" data-testid="new-folder-btn"><FolderPlus className="h-4 w-4" /></button>} />
          <SidebarItem active={activeFolder === null} onClick={() => setActiveFolder(null)} icon={<FileText className="h-4 w-4" />} label="All sheets" testid="folder-all" />
          {folders.map((f) => (
            <div key={f.id} className="group flex items-center">
              <SidebarItem active={activeFolder === f.id} onClick={() => setActiveFolder(f.id)} icon={<Folder className="h-4 w-4" />} label={f.name} testid={`folder-${f.id}`} />
              <button
                onClick={() => renameFolder(f.id, f.name)}
                className="mr-1 hidden text-[#A0ABC0] hover:text-[#D4AF37] group-hover:block"
                aria-label="Rename folder"
                title="Rename folder"
                data-testid={`folder-${f.id}-rename`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => deleteFolder(f.id)}
                className="mr-2 hidden text-[#A0ABC0] hover:text-red-400 group-hover:block"
                aria-label="Delete folder"
                title="Delete folder"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}

          <SectionHeader label={`Sheets · ${characters.length}/${MAX}`} />
          {filteredCharacters.length === 0 && <p className="px-3 text-xs text-[#A0ABC0]">No sheets yet.</p>}
          {filteredCharacters.map((c) => (
            <CharacterRow key={c.id} c={c} folders={folders} active={c.id === sheet?.id}
              onOpen={() => nav(`/app/sheet/${c.id}`)}
              onDelete={() => deleteSheet(c.id)}
              onClone={() => cloneSheet(c.id)}
              onSetStart={async () => { await api.post("/me/start-sheet", { sheet_id: c.id }); toast.success("Set as starting sheet"); }}
            />
          ))}

          <SectionHeader label={`Templates · ${templates.length}/${MAX}`} />
          {templates.length === 0 && <p className="px-3 text-xs text-[#A0ABC0]">No templates yet.</p>}
          {templates.map((t) => (
            <div key={t.id} className="group flex items-center">
              <button
                onClick={() => createCharacter(t)}
                disabled={charsFull}
                className="flex flex-1 items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-[#A0ABC0] hover:bg-white/5 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                title={charsFull ? `Sheet limit reached (${characters.length}/${MAX})` : `Create a new sheet from "${t.name}"`}
                data-testid={`template-${t.id}`}
              >
                <BookmarkPlus className="h-4 w-4" />
                <span className="truncate">{t.name}</span>
              </button>
              <button onClick={() => deleteSheet(t.id)} className="mr-2 hidden text-[#A0ABC0] hover:text-red-400 group-hover:block" aria-label="Delete template">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>

        <div className="border-t border-[#212A3E] px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="truncate text-sm text-white">{user?.name || user?.email}</div>
              <div className="truncate text-xs text-[#A0ABC0]">{user?.email}</div>
            </div>
            <button onClick={logout} className="text-[#A0ABC0] hover:text-red-400" data-testid="logout-btn" title="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        {/* Topbar */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#212A3E] bg-[#070A11]/85 px-6 py-3 backdrop-blur">
          <div className="flex min-w-0 items-center gap-3">
            {renaming ? (
              <Input
                autoFocus defaultValue={sheet?.name || ""}
                onBlur={(e) => renameSheet(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") renameSheet(e.target.value); if (e.key === "Escape") setRenaming(false); }}
                className="h-9 max-w-md border-[#212A3E] bg-[#0a0f1c] text-white"
                data-testid="sheet-rename-input"
              />
            ) : (
              <button onClick={() => setRenaming(true)} className="truncate text-left font-serif-display text-2xl text-white hover:text-[#D4AF37]" data-testid="sheet-title">
                {sheet?.name || "—"}
              </button>
            )}
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#A0ABC0]" />}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={editMode ? "default" : "ghost"}
              onClick={() => setEditMode((v) => !v)}
              className={editMode
                ? "bg-[#D4AF37] text-[#0b0f18] hover:bg-[#e7c75a]"
                : "text-white hover:text-[#D4AF37]"}
              data-testid="edit-mode-toggle"
              title={editMode ? "Click Done to lock structure" : "Edit labels, add/remove lines"}
            >
              <Pencil className="mr-1 h-4 w-4" /> {editMode ? "Done" : "Edit"}
            </Button>
            <Button variant="ghost" onClick={addBox} className="text-white hover:text-[#D4AF37]" data-testid="add-box-btn">
              <Plus className="mr-1 h-4 w-4" /> Add box
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-white" data-testid="sheet-menu-btn"><MoreVertical className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-[#212A3E] bg-[#101623] text-white">
                <DropdownMenuItem onClick={() => sheet && saveSheet(sheet)} data-testid="sheet-save-btn">
                  <Save className="mr-2 h-4 w-4" /> Save now
                </DropdownMenuItem>
                <DropdownMenuItem onClick={saveAsTemplate} data-testid="sheet-save-template-btn" disabled={tplsFull}>
                  <BookmarkPlus className="mr-2 h-4 w-4" /> Save as template{tplsFull ? ` (${templates.length}/${MAX})` : ""}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={setAsStart} data-testid="sheet-set-start-btn">
                  <Star className="mr-2 h-4 w-4" /> Set as starting sheet
                </DropdownMenuItem>
                <DropdownMenuItem onClick={shareSheet} data-testid="sheet-share-btn">
                  <Share2 className="mr-2 h-4 w-4" /> Share read-only link
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openShareList} data-testid="sheet-manage-shares-btn">
                  <Share2 className="mr-2 h-4 w-4" /> Manage share links
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#212A3E]" />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-[#A0ABC0]">Move to folder</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => moveToFolder(null)}>— None —</DropdownMenuItem>
                {folders.map((f) => (
                  <DropdownMenuItem key={f.id} onClick={() => moveToFolder(f.id)}>{f.name}</DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="bg-[#212A3E]" />
                <DropdownMenuItem onClick={() => sheet && deleteSheet(sheet.id)} className="text-red-400 focus:text-red-300" data-testid="sheet-delete-btn">
                  <Trash2 className="mr-2 h-4 w-4" /> Delete sheet
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {loading ? (
          <div className="flex h-[60vh] items-center justify-center text-[#A0ABC0]">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="px-6 pb-12">
            <SheetCanvas sheet={sheet} onChange={onSheetChange} editMode={editMode} />
          </div>
        )}
      </main>
    </div>
  );
}

function SectionHeader({ label, action }) {
  return (
    <div className="mt-5 flex items-center justify-between px-3 pb-2">
      <span className="font-mono-tight text-[10px] uppercase tracking-[0.24em] text-[#A0ABC0]">{label}</span>
      {action}
    </div>
  );
}

function SidebarItem({ active, onClick, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
        active ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-[#A0ABC0] hover:bg-white/5 hover:text-white"
      }`}
      data-testid={testid}
    >
      {icon}
      <span className="truncate">{label}</span>
      <ChevronRight className="ml-auto h-3 w-3 opacity-40" />
    </button>
  );
}

function CharacterRow({ c, folders, active, onOpen, onDelete, onClone, onSetStart }) {
  return (
    <div className="group flex items-center">
      <button
        onClick={onOpen}
        className={`flex flex-1 items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
          active ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-[#A0ABC0] hover:bg-white/5 hover:text-white"
        }`}
        data-testid={`character-${c.id}`}
      >
        <FileText className="h-4 w-4" />
        <span className="truncate">{c.name}</span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="mr-1 hidden text-[#A0ABC0] hover:text-white group-hover:block" data-testid={`character-${c.id}-menu`}>
            <MoreVertical className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-[#212A3E] bg-[#101623] text-white">
          <DropdownMenuItem onClick={onClone}>Clone</DropdownMenuItem>
          <DropdownMenuItem onClick={onSetStart}>Set as starting sheet</DropdownMenuItem>
          <DropdownMenuSeparator className="bg-[#212A3E]" />
          <DropdownMenuItem onClick={onDelete} className="text-red-400 focus:text-red-300">Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
